import { Button } from "@/components/ui/button";
import { MapPin, Utensils, Link, File, Calendar, Clock } from "lucide-react";
import { FoodDrinksActivity, ItineraryActivity } from "../models/activity.model";
import { BaseActivityCard } from "./BaseActivityCard";
import { DocumentationList } from "@/components/ui/documentation-list";
import { MediaGallery } from "@/components/ui/media-gallery";

interface FoodDrinksCardProps {
  activity: FoodDrinksActivity;
  onEdit: (activity: ItineraryActivity) => void;
  onDelete: (activity: ItineraryActivity) => void;
  onDuplicate?: (activity: ItineraryActivity) => void;
  onExpand?: () => void;
  isReadOnly?: boolean;
  onDragStart?: (activity: ItineraryActivity) => void;
  onDropBefore?: (sourceActivityId: string, targetActivityId?: string) => void;
  onDragEnd?: () => void;
}

const formatDateRange = (start_date: string, end_date: string) => {
    const start = new Date(start_date);
    const end = new Date(end_date);

    const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
    const startDay = start.getDate();
    const endDay = end.getDate();

    return `${startMonth} ${startDay} - ${endDay}`;
  };

export const FoodDrinksCard = ({
  activity,
  onEdit,
  onDelete,
  onDuplicate,
  onExpand,
  isReadOnly = false,
  onDragStart,
  onDropBefore,
  onDragEnd
}: FoodDrinksCardProps) => {
  const renderAlwaysVisible = () => (
    <>
      {/* Description */}
      {activity.description && (
        <p className="text-sm text-gray-600 leading-relaxed">{activity.description}</p>
      )}
    </>
  );

  const renderCollapsible = () => (
    <>

      {/* Media Gallery */}
      <MediaGallery media={activity.media || []} />

      {/* Documentation */}
      <DocumentationList documentation={activity.documentation || []} />

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          {/* Link Button */}
          {activity.link && (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-xl"
              onClick={() => window.open(activity.link, '_blank')}
            >
              <Link className="h-5 w-5 mr-2 flex-shrink-0" />
              <span className="truncate max-w-[200px] inline-block">
                {activity.link}
              </span>
            </Button>
          )}
        </div>
      </div>
    </>
  );

  const renderHeaderExtras = () => (
    <>
      {activity.start_date && (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          <span>{formatDateRange(activity.start_date, activity.end_date)}</span>
        </div>
      )}
      {activity.start_time && activity.end_time && (
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span>{activity.start_time} - {activity.end_time}</span>
        </div>
      )}
    </>
  );

  return (
    <BaseActivityCard
      activity={activity}
      onEdit={onEdit}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
      onDragStart={onDragStart}
      onDropBefore={onDropBefore}
      onDragEnd={onDragEnd}
      onExpand={onExpand}
      isReadOnly={isReadOnly}
    >
      {{
        alwaysVisible: (
          <>
            {renderAlwaysVisible()}
            {renderHeaderExtras()}
          </>
        ),
        collapsible: renderCollapsible()
      }}
    </BaseActivityCard>
  );
};
